/**
 * NavProvider — owns the nav state (`viewId` + `pageId`) so the Sidenav and
 * the page can stay in sync without prop-drilling.
 *
 * Pattern (from the mockup):
 *   - `viewId` = which level the sidebar is showing
 *   - `pageId` = which page is open on the right
 *   - Click a folder/group/bot in the items list → sets `viewId` (drill).
 *   - Click a leaf in the items list → sets `pageId` AND navigates to its URL.
 *   - Click a crumb → only sets `viewId` (sidebar moves up; page stays).
 *
 * Page bodies usually don't need to read this — they get their data from
 * the URL and their own hooks. The Sidenav is the primary consumer.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthnMe } from '../../AuthnMeProvider';
import {
  BotSummary,
  buildTree,
  crumbsFor,
  findPath,
  findPathByIdOrPage,
  isAdminOnly,
  levelChildren,
  levelLabel,
  NavNode,
} from './tree';
import { useNavBots } from './useNavBots';
import { markBotOpened } from './recentBots';
import { navigate as routerNavigate } from './useUrl';
import { usePreference } from '../../preferences';

interface NavContextValue {
  tree: NavNode;
  isAdmin: boolean;
  customerId?: string;
  botId?: string;
  botsLoading: boolean;
  viewId: string;
  pageId: string;
  /** Click an item — folder drills, leaf navigates. */
  navigateTo: (id: string) => void;
  /** Drill the sidebar to a folder/group without leaving the current page. */
  drillTo: (id: string) => void;
  /** Crumb click — moves sidebar view up; page unchanged. */
  crumbTo: (id: string) => void;
  // Derived for consumers:
  crumbs: NavNode[];
  items: NavNode[];
  sectionLabel: string;
}

const NavContext = createContext<NavContextValue | null>(null);

export interface NavProviderProps {
  customerId?: string;
  customerName?: string;
  botId?: string;
  botName?: string;
  /** All bots in the workspace — surfaces them inside "Your bots". */
  bots?: BotSummary[];
  /** Initial pageId — pick the one matching the URL the user landed on. */
  initialPageId?: string;
  /** Initial viewId — defaults to the parent of `initialPageId`. */
  initialViewId?: string;
  children: React.ReactNode;
}

/**
 * NavProvider — creates the nav context, OR passes through when one already
 * exists above it. The persistent `ShellLayout` mounts the real provider (so
 * the Sidenav survives navigation); the per-page `<NavProvider>` wrappers then
 * become no-op pass-throughs, their props ignored in favour of the hoisted,
 * URL-derived one. Standalone renders (tests) with no parent still get a real
 * provider.
 */
export function NavProvider(props: NavProviderProps) {
  const parent = useContext(NavContext);
  if (parent) return <>{props.children}</>;
  return <NavProviderInner {...props} />;
}

function NavProviderInner({
  customerId, customerName, botId, botName, bots,
  initialPageId = 'bot-overview', initialViewId,
  children,
}: NavProviderProps) {
  const authn = useAuthnMe();
  const roles = authn?.user?.roles ?? [];
  const isAdmin = roles.includes('owner') || roles.includes('admin');
  const [selectedBotId, setSelectedBotId] = usePreference('selectedBotId');
  // Scope the workspace by the current route, never the last-selected bot.
  // That preference can belong to another customer an admin visited, and it
  // survives sign-out. On Home, an empty scope must resolve to the caller.
  const queryNavBots = useNavBots(botId || '', customerId);
  const effectiveBots = useMemo(
    () => (bots && bots.length > 0
      ? bots
      : queryNavBots.bots.map((b) => ({ bot_id: b.bot_id, bot_name: b.bot_name }))),
    [bots, queryNavBots.bots],
  );
  const effectiveCustomerId = customerId || queryNavBots.customerId;
  const effectiveCustomerName = customerName || queryNavBots.customerName;
  const selectedBot = !botId && selectedBotId
    ? effectiveBots.find((b) => b.bot_id === selectedBotId)
    : undefined;
  const effectiveBotId = botId || selectedBot?.bot_id;
  const effectiveBotName = botId ? botName : selectedBot?.bot_name;

  useEffect(() => {
    if (!botId || selectedBotId === botId) return;
    void setSelectedBotId(botId).catch(() => {});
  }, [botId, selectedBotId, setSelectedBotId]);

  // Recency for the sidenav's home level ("your bots, last opened first").
  // Every bot-scoped page mounts a NavProvider with its botId, so this is the
  // one choke point that sees every bot the user opens.
  useEffect(() => { markBotOpened(botId); }, [botId]);

  const tree = useMemo(
    () => buildTree({
      customerId: effectiveCustomerId,
      customerName: effectiveCustomerName,
      botId: effectiveBotId,
      botName: effectiveBotName,
      bots: effectiveBots,
      isAdmin,
    }),
    [effectiveCustomerId, effectiveCustomerName, effectiveBotId, effectiveBotName, effectiveBots, isAdmin],
  );

  const [pageId, setPageId] = useState<string>(initialPageId);
  const [viewId, setViewId] = useState<string>(() => {
    if (initialViewId) return initialViewId;
    // `initialPageId` is sometimes a node id (e.g. 'bot:prompts') and
    // sometimes a page key (e.g. 'bot-prompts'). Try both so the sidebar
    // drills into the right parent regardless.
    const trail = findPathByIdOrPage(tree, initialPageId);
    if (trail && trail.length >= 2) return trail[trail.length - 2].id;
    return tree.id;
  });

  // Keep state in sync with the host page's props. Configure stays mounted
  // across `?tab=…` changes; without this, switching from Prompts → Voice
  // inside the same Settings section wouldn't update the sidebar's active
  // row. Effect is idempotent for sidebar-initiated nav (navigateTo already
  // sets the same values).
  useEffect(() => {
    setPageId(initialPageId);
    if (initialViewId) {
      setViewId(initialViewId);
      return;
    }
    const trail = findPathByIdOrPage(tree, initialPageId);
    if (!trail) { setViewId(tree.id); return; }
    const last = trail[trail.length - 1];
    // For leaves: show their siblings (parent's children).
    // For folder/group/bot/workspace nodes (e.g. "Your bots"): show their
    // OWN children, otherwise clicking the group would collapse the
    // sidebar back to the workspace level instead of drilling into it.
    if (last.kind === 'leaf') {
      setViewId(trail.length >= 2 ? trail[trail.length - 2].id : tree.id);
    } else {
      setViewId(last.id);
    }
  }, [initialPageId, initialViewId, tree]);

  const navigateTo = useCallback((id: string) => {
    if (isAdminOnly(tree, id) && !isAdmin) return;
    const trail = findPath(tree, id);
    if (!trail) return;
    const node = trail[trail.length - 1];

    // Pull the candidate URL up front. Any node that declares `to()` —
    // leaf OR folder (bot, Voice, Settings, …) — should navigate. Folders
    // also drill the sidebar; leaves move the sidebar to their siblings.
    const url = node.to?.({ botId: effectiveBotId, customerId: effectiveCustomerId });

    if (url) {
      setPageId(node.page || node.id);
      if (node.kind === 'leaf') {
        const parent = trail.length >= 2 ? trail[trail.length - 2] : tree;
        setViewId(parent.id);
      } else {
        setViewId(id);
      }
      // SPA navigation — pushState so the page doesn't reload. App.tsx
      // subscribes via useUrl() and re-renders, picking the right v2 page
      // for the new URL.
      if (typeof window !== 'undefined') {
        const here = window.location.pathname + window.location.search;
        if (here !== url) routerNavigate(url);
      }
      return;
    }

    // No URL — pure drill (e.g. workspace root, "Your bots" if it has no `to`).
    setViewId(id);
  }, [tree, isAdmin, effectiveBotId, effectiveCustomerId]);

  const drillTo = useCallback((id: string) => setViewId(id), []);
  const crumbTo = useCallback((id: string) => setViewId(id), []);

  const crumbs = useMemo(() => crumbsFor(tree, viewId), [tree, viewId]);
  const items = useMemo(() => levelChildren(tree, viewId, isAdmin), [tree, viewId, isAdmin]);
  const sectionLabel = useMemo(() => levelLabel(tree, viewId), [tree, viewId]);

  const value: NavContextValue = {
    tree, isAdmin, customerId: effectiveCustomerId, botId: effectiveBotId,
    botsLoading: !!queryNavBots.loading,
    viewId, pageId,
    navigateTo, drillTo, crumbTo,
    crumbs, items, sectionLabel,
  };

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

/** Null outside a <NavProvider> — lets legacy components detect the v2 shell. */
export function useOptionalNavTree(): NavContextValue | null {
  return useContext(NavContext);
}

export function useNavTree(): NavContextValue {
  const v = useContext(NavContext);
  if (!v) throw new Error('useNavTree must be used inside <NavProvider>');
  return v;
}
